import { useEffect, useRef, useState } from "react";
import logo from "../assets/Varroe Merchant.svg";
import logomark from "../assets/VarroeLogo.svg";

export default function Maintenance() {
  const contentRef = useRef<HTMLDivElement>(null);
  const logoRefs = useRef<Array<HTMLDivElement | null>>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const baseTransformsRef = useRef<Array<{ x: number; y: number }>>([]);
  const mousePositionRef = useRef<{ x: number; y: number } | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    // Always start with CSS animations for immediate feedback
    function useCSSAnimations() {
      // Animate content fade in
      if (contentRef.current) {
        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.style.transition =
              "opacity 0.8s ease-out, transform 0.8s ease-out";
            contentRef.current.style.opacity = "1";
            contentRef.current.style.transform = "translateY(0)";
          }
        }, 100);
      }

      // Animate floating logos with CSS
      logoRefs.current.forEach((logoEl, index) => {
        if (!logoEl) return;
        const delay = index * 0.2;

        // Create floating effect with CSS animation (but we'll control transform via JS)
        // We'll use a wrapper approach or disable animation when mouse is active

        // Fade in with delay
        setTimeout(() => {
          if (logoEl) {
            logoEl.style.transition = "opacity 1.5s ease-out";
            // Dark mode: 0.25 opacity, light mode: 0.3 opacity - much more visible
            const isDark = document.documentElement.classList.contains("dark");
            logoEl.style.opacity = isDark ? "0.25" : "0.3";
          }
        }, delay * 1000);
      });
    }

    // Start with CSS animations immediately
    useCSSAnimations();

    // Try to enhance with motion library if available
    import("motion")
      .then((motion) => {
        const animate = motion.animate || motion.default?.animate;

        if (!animate || typeof animate !== "function") {
          return; // Keep using CSS animations
        }

        // Enhance floating logos with motion
        logoRefs.current.forEach((logoEl, index) => {
          if (!logoEl) return;

          const randomDelay = index * 0.3;
          const randomDuration = 15 + Math.random() * 10;
          // const randomX = (Math.random() - 0.5) * 80;
          // const randomY = (Math.random() - 0.5) * 80;
          // const randomRotation = (Math.random() - 0.5) * 90;

          // Floating animation
          try {
            animate(
              logoEl,
              {
                // x: [randomX, randomX + (Math.random() - 0.5) * 60],
                // y: [randomY, randomY + (Math.random() - 0.5) * 60],
                // rotate: [
                //   randomRotation,
                //   randomRotation + (Math.random() - 0.5) * 45,
                // ],
              },
              {
                duration: randomDuration,
                delay: randomDelay,
                repeat: Infinity,
                // direction: "alternate",
                // easing: "ease-in-out",
              }
            );
          } catch (e) {
            // If animation fails, keep CSS animations
          }
        });

        // Enhance content animation
        if (contentRef.current) {
          try {
            animate(
              contentRef.current,
              {
                // opacity: [0, 1],
                //  y: [20, 0]
              },
              {
                duration: 0.8,
                // easing: "ease-out"
              }
            );
          } catch (e) {
            // If animation fails, keep CSS transition
          }
        }
      })
      .catch(() => {
        // CSS animations are already running, so we're good
      });

    // Mouse tracking for logo repulsion effect
    function handleMouseMove(e: MouseEvent) {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      mousePositionRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      // Use requestAnimationFrame for smooth updates
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        updateLogoPositions();
      });
    }

    function handleMouseLeave() {
      mousePositionRef.current = null;

      // Return logos to base positions
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        returnLogosToBase();
      });
    }

    function updateLogoPositions() {
      if (!mousePositionRef.current || !containerRef.current) return;

      const mouseX = mousePositionRef.current.x;
      const mouseY = mousePositionRef.current.y;
      const repulsionDistance = 150; // Distance at which logos start to move
      const maxRepulsion = 60; // Maximum distance logos can move away

      logoRefs.current.forEach((logoEl, index) => {
        if (!logoEl) return;

        const rect = logoEl.getBoundingClientRect();
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) return;
        // Calculate logo center position relative to container
        const logoX = rect.left + rect.width / 2 - containerRect.left;
        const logoY = rect.top + rect.height / 2 - containerRect.top;

        const dx = logoX - mouseX;
        const dy = logoY - mouseY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Pause CSS animation when mouse is active
        if (distance < repulsionDistance) {
          logoEl.style.animationPlayState = "paused";
        }

        if (distance < repulsionDistance && distance > 0) {
          // Calculate repulsion force (stronger when closer)
          const force = (1 - distance / repulsionDistance) * maxRepulsion;
          const angle = Math.atan2(dy, dx);

          // Calculate new offset (move away from mouse)
          const offsetX = Math.cos(angle) * force;
          const offsetY = Math.sin(angle) * force;

          // Get current transform or use base
          const base = baseTransformsRef.current[index] || { x: 0, y: 0 };
          logoEl.style.transform = `translate(${base.x + offsetX}px, ${
            base.y + offsetY
          }px)`;
          logoEl.style.transition = "transform 0.15s ease-out";
        } else {
          // Resume animation and return to base position
          logoEl.style.animationPlayState = "running";
          const base = baseTransformsRef.current[index] || { x: 0, y: 0 };
          logoEl.style.transform = `translate(${base.x}px, ${base.y}px)`;
          logoEl.style.transition = "transform 0.3s ease-out";
        }
      });
    }

    function returnLogosToBase() {
      logoRefs.current.forEach((logoEl, index) => {
        if (!logoEl) return;
        // Resume CSS animation
        logoEl.style.animationPlayState = "running";
        const base = baseTransformsRef.current[index] || { x: 0, y: 0 };
        logoEl.style.transform = `translate(${base.x}px, ${base.y}px)`;
        logoEl.style.transition = "transform 0.5s ease-out";
      });
    }

    // Initialize base positions (logos start at 0,0 transform offset)
    logoRefs.current.forEach((_, index) => {
      if (!baseTransformsRef.current[index]) {
        baseTransformsRef.current[index] = { x: 0, y: 0 };
      }
    });

    // Add mouse event listeners
    if (containerRef.current) {
      containerRef.current.addEventListener("mousemove", handleMouseMove);
      containerRef.current.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener("mousemove", handleMouseMove);
        containerRef.current.removeEventListener(
          "mouseleave",
          handleMouseLeave
        );
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isClient]);

  // Generate logo positions
  const logoPositions = [
    { top: "10%", left: "10%" },
    { top: "20%", right: "15%" },
    { top: "60%", left: "5%" },
    { top: "70%", right: "10%" },
    { bottom: "15%", left: "20%" },
    { bottom: "25%", right: "25%" },
    { top: "40%", left: "80%" },
    { bottom: "40%", right: "5%" },
  ];

  return (
    <>
      <div
        ref={containerRef}
        className="relative min-h-screen flex items-center justify-center bg-dark text-white px-6 py-20 overflow-hidden"
      >
        {/* Floating background logos */}
        <div className="absolute inset-0 pointer-events-none">
          {logoPositions.map((position, index) => (
            <div
              key={index}
              ref={(el) => {
                if (el && !logoRefs.current[index]) {
                  logoRefs.current[index] = el;
                }
              }}
              className="absolute opacity-0 animate-float-maintenance"
              style={{
                ...position,
                willChange: "transform, opacity",
              }}
            >
              <img
                src={index % 2 === 0 ? logo : logomark}
                alt=""
                width={80}
                height={80}
                className="w-16 h-16 md:w-20 md:h-20"
                style={{ filter: "blur(0.3px)" }}
              />
            </div>
          ))}
        </div>

        {/* Main content */}
        <div
          ref={contentRef}
          className="relative z-10 max-w-md w-full text-center space-y-6"
          style={{ opacity: 0, transform: "translateY(20px)" }}
        >
          <h3 className="font-bold font-outfit text-2xl lg:text-4xl">
            Varroe is currently being updated
          </h3>
          <p className="font-inter text-base lg:text-lg">
            {
              "We're taking a short pause to refine our platform based on customerfeedback."
            }
          </p>
          <p className="font-inter text-sm lg:text-base text-gray-600 dark:text-gray-400">
            For inquiries, please contact{" "}
            <a
              href="mailto:v-team@varroe.com"
              className="underline hover:text-v-purple dark:hover:text-v-purple transition-colors"
            >
              v-team@varroe.com
            </a>
          </p>
        </div>
      </div>
    </>
  );
}
